import { useCallback, useRef, useState } from "react";

import { executeSearch } from "./search-api";
import {
  SearchCriteria,
  SearchEntityKey,
  SearchResultsResponse,
} from "./search-metadata.types";

export interface UseSearchQueryResult {
  results: SearchResultsResponse | null;
  isSearching: boolean;
  error: string | null;
  // eslint-disable-next-line no-unused-vars
  search(entity: SearchEntityKey, criteria: SearchCriteria): Promise<void>;
  clear(): void;
}

/**
 * Runs entity searches against the search API (mocked until the backend
 * endpoint exists) and tracks loading/error/result state.
 */
export const useSearchQuery = (): UseSearchQueryResult => {
  const [results, setResults] = useState<SearchResultsResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const search = useCallback(
    async (entity: SearchEntityKey, criteria: SearchCriteria) => {
      const requestId = ++requestIdRef.current;
      setIsSearching(true);
      setError(null);
      try {
        const response = await executeSearch(entity, criteria);
        if (requestId !== requestIdRef.current) return;
        setResults(response);
      } catch (err: unknown) {
        if (requestId !== requestIdRef.current) return;
        setResults(null);
        setError(err instanceof Error ? err.message : "Search failed");
      } finally {
        if (requestId === requestIdRef.current) {
          setIsSearching(false);
        }
      }
    },
    [],
  );

  const clear = useCallback(() => {
    // Invalidate any in-flight request so it cannot resurrect stale results
    requestIdRef.current += 1;
    setResults(null);
    setError(null);
    setIsSearching(false);
  }, []);

  return { results, isSearching, error, search, clear };
};
